import { useEffect, useState, useCallback } from "react";
import {
  LayoutDashboard, Plus, Trash2, ChevronDown, ChevronRight,
  Play, RefreshCw, ToggleLeft, ToggleRight, Pencil, Sparkles, GripVertical,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { dashboardsApi, adminApi, chatApi } from "../../api";
import { LoadingText, SchedulePicker, Select, ChipInput } from "../ui";
import { describeCron, isValidCron } from "../../utils";
import type { AppUser } from "../../types/user";

// ── Types ──────────────────────────────────────────────────────────────────

interface ScheduledTile {
  id: string;
  title: string;
  subtitle?: string | null;
  metric_sql: string;
  datasource_key?: string | null;
  chart_type: string;
  x_col?: string | null;
  y_col?: string | null;
  width: string;
  position: number;
  is_currency?: boolean;
}

interface ScheduledDashboardItem {
  id: string;
  name: string;
  description?: string | null;
  cron_expression: string;
  is_active?: boolean;
  shared_with_all?: boolean;
  deliver_email?: boolean;
  deliver_slack?: boolean;
  recipient_emails?: string[];
  slack_channel_ids?: string[];
  tiles?: ScheduledTile[];
}

interface TileFormState {
  title: string;
  subtitle: string;
  metric_sql: string;
  datasource_key: string;
  chart_type: string;
  x_col: string;
  y_col: string;
  width: string;
  position: number;
  is_currency: boolean;
}

type DashFormState = {
  name: string;
  description: string;
  cron_expression: string;
  is_active: boolean;
  shared_with_all: boolean;
  deliver_email: boolean;
  deliver_slack: boolean;
  recipient_emails: string[];
  slack_channel_ids: string[];
};

type PreviewState = { error?: string; rows?: Record<string, unknown>[] } | null;

// ── Helpers ──────────────────────────────────────────────────────────────────

const CHART_TYPES = [
  { value: "number",          label: "KPI / Number" },
  { value: "bar",             label: "Bar chart" },
  { value: "line",            label: "Line chart" },
  { value: "table",           label: "Table" },
  { value: "analysis",        label: "AI Analysis" },
  { value: "recommendations", label: "AI Recommendations" },
];

const INSIGHT_TYPES = new Set(["analysis", "recommendations"]);

const WIDTHS = [
  { value: "quarter", label: "Quarter width (1/4)" },
  { value: "third",   label: "Third width (1/3)" },
  { value: "half",    label: "Half width (1/2)" },
  { value: "full",    label: "Full width" },
];

const inputCls = "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

// ── Tile form ─────────────────────────────────────────────────────────────────

const DEFAULT_TILE: TileFormState = {
  title: "", subtitle: "", metric_sql: "", datasource_key: "",
  chart_type: "number", x_col: "", y_col: "", width: "half", position: 0, is_currency: false,
};

interface TileFormProps {
  initial?: TileFormState;
  onSave: (payload: Record<string, unknown>) => Promise<void> | void;
  onCancel: () => void;
}

function TileForm({ initial, onSave, onCancel }: TileFormProps) {
  const [form, setForm] = useState<TileFormState>(initial ?? DEFAULT_TILE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>(null);
  const [previewing, setPreviewing] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [showGen, setShowGen] = useState(false);

  function set<K extends keyof TileFormState>(field: K, val: TileFormState[K]) {
    setForm((f) => ({ ...f, [field]: val }) as TileFormState);
  }

  async function handleGenerate() {
    if (!genPrompt.trim()) return;
    setGenerating(true);
    try {
      const res = await chatApi.generateSql(genPrompt.trim(), form.datasource_key || null);
      set("metric_sql", res.data.sql);
      setShowGen(false);
      setGenPrompt("");
      setPreview(null);
    } catch { return; } finally {
      setGenerating(false);
    }
  }

  const isInsight = INSIGHT_TYPES.has(form.chart_type);
  const needsAxes = !isInsight && (form.chart_type === "bar" || form.chart_type === "line");

  async function handlePreview() {
    if (!form.metric_sql.trim()) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await dashboardsApi.previewSql(form.metric_sql, form.datasource_key || null, { suppressErrorToast: true });
      setPreview(res.data);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setPreview({ error: detail ?? "Query failed" });
    } finally {
      setPreviewing(false);
    }
  }

  async function handleSave() {
    if (!form.title.trim()) { setError("Title is required"); return; }
    if (!isInsight && !form.metric_sql.trim()) { setError("SQL is required"); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        metric_sql: form.metric_sql.trim(),
        datasource_key: form.datasource_key.trim() || null,
        x_col: form.x_col.trim() || null,
        y_col: form.y_col.trim() || null,
      });
    } catch { return; } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Title *</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Total revenue today" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Subtitle</label>
          <input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Optional context line" className={inputCls} />
        </div>
      </div>

      {isInsight ? (
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Focus instructions <span className="font-normal text-gray-400">optional — guide the AI's focus</span>
          </label>
          <textarea
            value={form.metric_sql}
            onChange={(e) => set("metric_sql", e.target.value)}
            rows={2}
            placeholder={form.chart_type === "analysis"
              ? "e.g. Focus on revenue trends and highlight any drops vs prior period"
              : "e.g. Focus on player acquisition and retention improvements"}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            This tile reads all other tiles' data when the snapshot runs and generates AI commentary automatically. No SQL needed.
          </p>
        </div>
      ) : (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-semibold text-gray-700">SQL * <span className="font-normal text-gray-400">must return data as of the run time</span></label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowGen((v) => !v)}
                className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700 hover:bg-blue-100"
              >
                <Sparkles size={11} />
                Generate with agent
              </button>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewing || !form.metric_sql.trim()}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                {previewing ? <RefreshCw size={11} className="animate-spin" /> : <Play size={11} />}
                Test SQL
              </button>
            </div>
          </div>

          {showGen && (
            <div className="mb-2 rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
              <p className="text-xs text-blue-700 font-medium">Describe what this tile should show — the agent will write the SQL using your schema.</p>
              <p className="text-xs text-blue-600">Tip: for a KPI with day-over-day comparison, say so — e.g. "Yesterday's GGR and the day before for comparison"</p>
              <textarea
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
                rows={2}
                placeholder="e.g. Yesterday's GGR vs the day before"
                className="w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || !genPrompt.trim()}
                  className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#16294d] disabled:opacity-50"
                >
                  {generating ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
                  {generating ? "Generating…" : "Generate SQL"}
                </button>
                <button type="button" onClick={() => setShowGen(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
            </div>
          )}

          <textarea
            value={form.metric_sql}
            onChange={(e) => set("metric_sql", e.target.value)}
            rows={4}
            placeholder="SELECT SUM(revenue) FROM orders WHERE date = CURRENT_DATE"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-mono text-xs text-gray-900 placeholder-gray-400 leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        {preview && (
          <div className="mt-2 rounded-lg border border-gray-200 bg-white">
            {preview.error ? (
              <p className="px-3 py-2 text-xs text-red-600">{preview.error}</p>
            ) : preview.rows?.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">Query returned no rows.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>{Object.keys(preview.rows?.[0] ?? {}).map((c) => <th key={c} className="whitespace-nowrap px-3 py-1.5 text-left font-semibold text-gray-600">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(preview.rows ?? []).map((row, i) => (
                      <tr key={i} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                        {Object.values(row).map((v, j) => <td key={j} className="whitespace-nowrap px-3 py-1 text-gray-700">{String(v ?? "")}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Display as</label>
          <Select value={form.chart_type} onChange={(e) => set("chart_type", e.target.value)} className="w-full">
            {CHART_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </div>
        {needsAxes && (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">X column</label>
              <input value={form.x_col} onChange={(e) => set("x_col", e.target.value)} placeholder="e.g. date" className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">Y column</label>
              <input value={form.y_col} onChange={(e) => set("y_col", e.target.value)} placeholder="e.g. revenue" className={inputCls} />
            </div>
          </>
        )}
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Width</label>
          <Select value={form.width} onChange={(e) => set("width", e.target.value)} className="w-full">
            {WIDTHS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </Select>
        </div>
        {!isInsight && (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">Datasource key</label>
              <input value={form.datasource_key} onChange={(e) => set("datasource_key", e.target.value)} placeholder="Leave blank for default" className={inputCls} />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input
                id="is_currency"
                type="checkbox"
                checked={!!form.is_currency}
                onChange={(e) => set("is_currency", e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="is_currency" className="text-xs font-semibold text-gray-700 cursor-pointer select-none">
                Currency values (show £ symbol)
              </label>
            </div>
          </>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#16294d] disabled:opacity-50">
          {saving ? "Saving…" : "Save tile"}
        </button>
        <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Dashboard form ────────────────────────────────────────────────────────────

const DEFAULT_DASH: DashFormState = {
  name: "", description: "", cron_expression: "0 5 * * *", is_active: true, shared_with_all: true,
  deliver_email: false, deliver_slack: false, recipient_emails: [], slack_channel_ids: [],
};

interface DashboardFormProps {
  initial?: ScheduledDashboardItem;
  users: AppUser[];
  selfServe?: boolean;
  onSave: (form: DashFormState, shareIds: string[]) => Promise<void> | void;
  onCancel: () => void;
}

function DashboardForm({ initial, users, selfServe = false, onSave, onCancel }: DashboardFormProps) {
  const [form, setForm] = useState<DashFormState>(
    initial
      ? {
          name: initial.name,
          description: initial.description ?? "",
          cron_expression: initial.cron_expression,
          is_active: initial.is_active ?? true,
          shared_with_all: initial.shared_with_all ?? true,
          deliver_email: initial.deliver_email ?? false,
          deliver_slack: initial.deliver_slack ?? false,
          recipient_emails: initial.recipient_emails ?? [],
          slack_channel_ids: initial.slack_channel_ids ?? [],
        }
      : { ...DEFAULT_DASH, shared_with_all: !selfServe },
  );
  const [shareIds, setShareIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof DashFormState>(field: K, val: DashFormState[K]) {
    setForm((f) => ({ ...f, [field]: val }) as DashFormState);
  }

  function toggleUser(uid: string) {
    setShareIds((prev) => prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]);
  }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!isValidCron(form.cron_expression)) { setError("That schedule isn't a valid cron expression"); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(form, shareIds);
    } catch { return; } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Name *</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Daily Morning Briefing" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Description</label>
          <input value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} placeholder="Optional" className={inputCls} />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold text-gray-700">Schedule (UTC)</label>
        <SchedulePicker
          value={form.cron_expression}
          onChange={(cron) => set("cron_expression", cron)}
        />
      </div>

      <div>
        <label className="mb-2 block text-xs font-semibold text-gray-700">Visibility</label>
        {selfServe ? (
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked={!form.shared_with_all} onChange={() => set("shared_with_all", false)} className="text-blue-600" />
              Only me
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked={form.shared_with_all} onChange={() => set("shared_with_all", true)} className="text-blue-600" />
              All users
            </label>
          </div>
        ) : (
          <>
            <div className="flex gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={form.shared_with_all} onChange={() => set("shared_with_all", true)} className="text-blue-600" />
                All users
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={!form.shared_with_all} onChange={() => set("shared_with_all", false)} className="text-blue-600" />
                Specific users only
              </label>
            </div>
            {!form.shared_with_all && (
              <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                {users.map((u) => (
                  <label key={u.id} className="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={shareIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    {u.email}
                    {u.role?.name && <span className="ml-auto text-xs text-gray-400">{u.role.name}</span>}
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="rounded border-gray-300 text-blue-600" />
        Active (schedule will run)
      </label>

      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div>
          <p className="text-xs font-semibold text-gray-700">Automatic delivery</p>
          <p className="text-xs text-gray-400">Send each scheduled snapshot out by email and/or Slack. Manual "Run now" never sends.</p>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.deliver_email} onChange={(e) => set("deliver_email", e.target.checked)} className="rounded border-gray-300 text-blue-600" />
          Email the snapshot
        </label>
        {form.deliver_email && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Recipient emails</label>
            <ChipInput
              values={form.recipient_emails}
              onChange={(v) => set("recipient_emails", v)}
              placeholder="name@company.com — press Enter"
              validate={(v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)}
            />
          </div>
        )}

        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.deliver_slack} onChange={(e) => set("deliver_slack", e.target.checked)} className="rounded border-gray-300 text-blue-600" />
          Post to Slack
        </label>
        {form.deliver_slack && (
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Slack channel IDs</label>
            <ChipInput
              values={form.slack_channel_ids}
              onChange={(v) => set("slack_channel_ids", v)}
              placeholder="C0123456789 — press Enter"
            />
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#16294d] disabled:opacity-50">
          {saving ? "Saving…" : "Save dashboard"}
        </button>
        <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Sortable tile row ─────────────────────────────────────────────────────────

interface SortableTileRowProps {
  tile: ScheduledTile;
  editId: string | null;
  setEditId: (id: string | null) => void;
  onUpdate: (id: string, payload: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
}

function SortableTileRow({ tile, editId, setEditId, onUpdate, onDelete }: SortableTileRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tile.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  if (editId === tile.id) {
    return (
      <div ref={setNodeRef} style={style}>
        <TileForm
          initial={{
            title: tile.title,
            subtitle: tile.subtitle ?? "",
            metric_sql: tile.metric_sql,
            datasource_key: tile.datasource_key ?? "",
            chart_type: tile.chart_type,
            x_col: tile.x_col ?? "",
            y_col: tile.y_col ?? "",
            width: tile.width,
            position: tile.position,
            is_currency: tile.is_currency ?? false,
          }}
          onSave={(p) => onUpdate(tile.id, p)}
          onCancel={() => setEditId(null)}
        />
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <button
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab touch-none text-gray-300 hover:text-gray-500 active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical size={15} />
      </button>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-800">{tile.title}</span>
        {tile.subtitle && <span className="ml-2 text-xs text-gray-400">{tile.subtitle}</span>}
        <div className="mt-0.5 flex gap-3 text-xs text-gray-400">
          <span className="capitalize">{tile.chart_type}</span>
          <span>·</span>
          <span>{tile.width}</span>
          <span>·</span>
          <span className="truncate font-mono">{tile.metric_sql.slice(0, 60)}{tile.metric_sql.length > 60 ? "…" : ""}</span>
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <button onClick={() => setEditId(tile.id)} className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600">
          <Pencil size={13} />
        </button>
        <button onClick={() => onDelete(tile.id)} className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Tile list ─────────────────────────────────────────────────────────────────

function TileList({ dashboardId, onChanged }: { dashboardId: string; onChanged?: () => void }) {
  const [tiles, setTiles] = useState<ScheduledTile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(() => {
    dashboardsApi.listTiles(dashboardId)
      .then((r) => setTiles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dashboardId]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(payload: Record<string, unknown>) {
    await dashboardsApi.addTile(dashboardId, { ...payload, position: tiles.length });
    setShowForm(false);
    load();
    onChanged?.();
  }

  async function handleUpdate(tileId: string, payload: Record<string, unknown>) {
    await dashboardsApi.updateTile(dashboardId, tileId, payload);
    setEditId(null);
    load();
  }

  async function handleDelete(tileId: string) {
    if (!confirm("Delete this tile?")) return;
    await dashboardsApi.deleteTile(dashboardId, tileId);
    load();
    onChanged?.();
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tiles.findIndex((t) => t.id === active.id);
    const newIndex = tiles.findIndex((t) => t.id === over.id);
    const reordered = arrayMove(tiles, oldIndex, newIndex).map((t, idx) => ({ ...t, position: idx }));
    setTiles(reordered);

    // Persist updated positions for all tiles that moved
    await Promise.all(
      reordered
        .filter((t, idx) => tiles[idx]?.id !== t.id)
        .map((t) => dashboardsApi.updateTile(dashboardId, t.id, {
          title: t.title, subtitle: t.subtitle ?? null, metric_sql: t.metric_sql,
          datasource_key: t.datasource_key ?? null, chart_type: t.chart_type,
          x_col: t.x_col ?? null, y_col: t.y_col ?? null,
          width: t.width, position: t.position, is_currency: t.is_currency ?? false,
        }))
    );
  }

  if (loading) return <p className="py-2 text-xs text-gray-400">Loading tiles…</p>;

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tiles.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tiles.map((tile) => (
            <SortableTileRow
              key={tile.id}
              tile={tile}
              editId={editId}
              setEditId={setEditId}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      {showForm ? (
        <TileForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600"
        >
          <Plus size={13} />
          Add tile
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ScheduledDashboards({ selfServe = false }: { selfServe?: boolean } = {}) {
  const [dashboards, setDashboards] = useState<ScheduledDashboardItem[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [runResult, setRunResult] = useState<Record<string, string | null>>({});

  const load = useCallback(() => {
    if (selfServe) {
      dashboardsApi.list("mine")
        .then((dRes) => setDashboards(Array.isArray(dRes.data) ? dRes.data : []))
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }
    Promise.all([
      dashboardsApi.list("all"),
      adminApi.listUsers(),
    ]).then(([dRes, uRes]) => {
      setDashboards(Array.isArray(dRes.data) ? dRes.data : []);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selfServe]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(form: DashFormState, shareIds: string[]) {
    const res = await dashboardsApi.create(form);
    if (!form.shared_with_all && shareIds.length > 0) {
      await dashboardsApi.setShares(res.data.id, shareIds);
    }
    setShowCreate(false);
    load();
  }

  async function handleUpdate(id: string, form: DashFormState, shareIds: string[]) {
    await dashboardsApi.update(id, form);
    if (!selfServe && !form.shared_with_all) {
      await dashboardsApi.setShares(id, shareIds);
    }
    setEditId(null);
    load();
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this dashboard and all its snapshots?")) return;
    await dashboardsApi.remove(id);
    load();
  }

  async function handleToggle(d: ScheduledDashboardItem) {
    await dashboardsApi.update(d.id, { ...d, is_active: !d.is_active });
    load();
  }

  async function handleRun(id: string) {
    setRunning((r) => ({ ...r, [id]: true }));
    setRunResult((r) => ({ ...r, [id]: null }));
    try {
      await dashboardsApi.triggerRun(id);
      // Poll for snapshot completion (up to 90s)
      // eslint-disable-next-line react-hooks/purity -- runs in a click handler, not during render
      const deadline = Date.now() + 90_000;
      // eslint-disable-next-line react-hooks/purity -- runs in a click handler, not during render
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2500));
        const snaps = await dashboardsApi.adminSnapshots(id);
        const latest = snaps.data?.[0];
        if (latest && latest.status !== "running") {
          setRunResult((r) => ({ ...r, [id]: latest.status }));
          load();
          break;
        }
      }
    } catch {
      setRunResult((r) => ({ ...r, [id]: "error" }));
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
      setTimeout(() => setRunResult((r) => ({ ...r, [id]: null })), 8000);
    }
  }

  if (loading) return <LoadingText />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {selfServe ? "My scheduled dashboards" : "Scheduled dashboards"}
          </h2>
          <p className="text-xs text-gray-500">
            {selfServe
              ? "Build a dashboard, pick a schedule, and have it emailed or posted to Slack automatically."
              : "Run SQL queries on a schedule and share the results with your team."}
          </p>
        </div>
        {!showCreate && (
          <button
            onClick={() => { setShowCreate(true); setEditId(null); }}
            className="flex items-center gap-2 rounded-lg bg-[#1D345F] px-3 py-2 text-sm font-medium text-white hover:bg-[#16294d]"
          >
            <Plus size={14} />
            New dashboard
          </button>
        )}
      </div>

      {showCreate && (
        <div className="mb-4">
          <DashboardForm users={users} selfServe={selfServe} onSave={handleCreate} onCancel={() => setShowCreate(false)} />
        </div>
      )}

      {dashboards.length === 0 && !showCreate ? (
        <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center">
          <LayoutDashboard size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">No scheduled dashboards yet.</p>
          <p className="mt-1 text-xs text-gray-400">Create one to start running SQL queries on a schedule.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {dashboards.map((d) => (
            <div key={d.id} className={`rounded-xl border bg-white ${d.is_active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
              {editId === d.id ? (
                <div className="p-4">
                  <DashboardForm
                    initial={d}
                    users={users}
                    selfServe={selfServe}
                    onSave={(form, shareIds) => handleUpdate(d.id, form, shareIds)}
                    onCancel={() => setEditId(null)}
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 p-4">
                    <button
                      onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                      title={expandedId === d.id ? "Collapse" : "Expand to manage tiles"}
                      className="flex items-center gap-1 rounded px-1 py-1 text-gray-400 hover:text-gray-600"
                    >
                      {expandedId === d.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">{d.name}</span>
                        {!d.is_active && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">paused</span>}
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                          {d.shared_with_all ? "All users" : "Restricted"}
                        </span>
                      </div>
                      {d.description && <p className="mt-0.5 text-xs text-gray-500">{d.description}</p>}
                      <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-400">
                        <span>{describeCron(d.cron_expression) ?? d.cron_expression}</span>
                        <span>·</span>
                        <span
                        className="cursor-pointer underline-offset-2 hover:text-blue-600 hover:underline"
                        onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
                      >
                        {d.tiles?.length ?? 0} tile{d.tiles?.length !== 1 ? "s" : ""} — click › to manage
                      </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => handleRun(d.id)}
                        disabled={running[d.id]}
                        title="Run now"
                        className={`flex items-center gap-1 rounded px-2 py-1.5 text-xs disabled:opacity-50 ${
                          runResult[d.id] === "completed" ? "text-green-600" :
                          runResult[d.id] === "partial"   ? "text-amber-600" :
                          runResult[d.id] === "failed" || runResult[d.id] === "error" ? "text-red-600" :
                          "text-gray-500 hover:bg-blue-50 hover:text-blue-600"
                        }`}
                      >
                        {running[d.id] ? <RefreshCw size={13} className="animate-spin" /> : <Play size={13} />}
                        {running[d.id] ? "Running…" :
                          runResult[d.id] === "completed" ? "Done ✓" :
                          runResult[d.id] === "partial"   ? "Partial ⚠" :
                          runResult[d.id] === "failed" || runResult[d.id] === "error" ? "Failed ✗" :
                          "Run now"}
                      </button>
                      <button
                        onClick={() => handleToggle(d)}
                        title={d.is_active ? "Pause" : "Resume"}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        {d.is_active ? <ToggleRight size={16} className="text-green-500" /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => setEditId(d.id)}
                        className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(d.id)}
                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {expandedId === d.id && (
                    <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Tiles</p>
                      <TileList dashboardId={d.id} onChanged={load} />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
